import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
  Backdrop,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  keyframes,
  TextField,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Stop as StopIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Timer as TimerIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
  Telegram as TelegramIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { selectLabels } from 'state/dictionary/dictionarySlice';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { Logger } from 'utils/Logger';
import { handleApiError } from 'utils/ErrorHandler';
import { formatUtcToLocal } from 'utils/TimezoneUtils';
import { apiClient } from 'utils/ApiClient';
import { CAMPAIGN_RUNNER_INDIVIDUAL_URL, CAMPAIGN_RUNNER_STATS_URL, GET_COLLECTION_DATASETS_URL, GET_ALL_COMPANY_USERS_URL, GET_DATASET_COUNT_URL } from 'envConfig';
import { CampaignRunner } from 'modules/Interfaces/InstanUpdates/CampaignRunner';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { CampaignInformation } from './ReportingPanels/CampaignInformation';
import { ExecutionStatistics } from './ReportingPanels/ExecutionStatistics';
import { FilteringRules } from './ReportingPanels/FilteringRules';
import { PerformanceReview } from './ReportingPanels/PerformanceReview';
import { SuccessVsFailed } from './ReportingPanels/SuccessVsFailed';
import { ExecutionLogPanel } from './ReportingPanels/ExecutionLog';

interface ExecutionLogEntry {
  campaignId?: string;
  contactId: string;
  senderPhoneNumber?: string;
  receiverPhoneNumber?: string;
  receiverEmail?: string;
  senderEmail?: string;
  status: 'pending' | 'success' | 'failed' | 'processing' | 'cancelled' | 'skipped' | 'dispatched';
  message: string;
  timestamp: string;
  error?: string;
  addedAt?: string;
}

interface CampaignStats {
  totalContacts: number;
  processedContacts: number;
  successContacts: number;
  failedContacts: number;
  successRate: string;
  skippedContacts?: number;
  cancelledContacts?: number;
  emailReadCount?: number;
  /** SMS: count of logs with analytics.deliveryTimestamp (used for Success bar) */
  deliverySuccessCount?: number;
  /** SMS: count of logs with analytics.readTimestamp (used for Read bar) */
  smsReadCount?: number;
  /** SMS: sum of smsCreditCost for all success logs */
  totalSmsCreditCost?: number;
}

interface User {
  _id: string;
  name: string;
  email: string;
}

interface DataSet {
  _id: string;
  collectionName: string;
  contactsCount?: number;
}

// Helper function to get execution status chip
const getExecutionStatusChip = (status: 'pending' | 'processing' | 'success' | 'failed' | 'cancelled' | 'skipped' | 'dispatched') => {
  const statusConfig = {
    pending: { color: 'warning' as const, icon: <ScheduleIcon />, label: 'Pending' },
    processing: { color: 'info' as const, icon: <PlayArrowIcon />, label: 'Processing' },
    success: { color: 'success' as const, icon: <SuccessIcon />, label: 'Success' },
    failed: { color: 'error' as const, icon: <FailedIcon />, label: 'Failed' },
    cancelled: { color: 'error' as const, icon: <CancelIcon />, label: 'Cancelled' },
    skipped: { color: 'default' as const, icon: <CloseIcon />, label: 'Skipped' },
    dispatched: { color: 'info' as const, icon: <PlayArrowIcon />, label: 'Dispatched' },
  };

  const config = statusConfig[status];
  
  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

// Execution Log Table Component
const ExecutionLogTable: React.FC<{ logs: any[]; onOpenLog: (logId: string) => void; templateType?: string | null }> = ({ logs, onOpenLog, templateType }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortColumn, setSortColumn] = useState<string | null>('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const isMail = String(templateType || '').toLowerCase() === 'mail';

  const handleSort = (column: string): void => {
    const isAsc = sortColumn === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handleColumnFilterChange = (column: string, value: string): void => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
    setPage(0);
  };

  const filteredAndSortedLogs = useMemo(() => {
    let filtered = logs.filter(log => {
      // Global search
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const searchableFields = isMail
          ? [
              log.status,
              log.contactId,
              log.receiverEmail,
              log.message,
              log.error || ''
            ]
          : [
              log.status,
              log.contactId,
              log.senderPhoneNumber,
              log.receiverPhoneNumber,
              log.message,
              log.error || ''
            ];
        if (!searchableFields.some(field => field.toLowerCase().includes(searchLower))) {
          return false;
        }
      }

      // Column filters
      return Object.entries(columnFilters).every(([column, filterValue]) => {
        if (!filterValue) return true;
        const logValue = String(log[column as keyof ExecutionLogEntry] || '').toLowerCase();
        return logValue.includes(filterValue.toLowerCase());
      });
    });

    // Sort
    if (sortColumn) {
      filtered.sort((a, b) => {
        const aValue = String(a[sortColumn as keyof ExecutionLogEntry] || '').toLowerCase();
        const bValue = String(b[sortColumn as keyof ExecutionLogEntry] || '').toLowerCase();
        
        if (aValue < bValue) {
          return sortOrder === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortOrder === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [logs, searchQuery, columnFilters, sortColumn, sortOrder]);

  const paginatedLogs = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredAndSortedLogs.slice(startIndex, endIndex);
  }, [filteredAndSortedLogs, page, rowsPerPage]);

  const columns = isMail
    ? [
        { key: 'timestamp', label: 'Timestamp' },
        { key: 'status', label: 'Status' },
        { key: 'contactId', label: 'Contact ID' },
        { key: 'receiverEmail', label: 'Receiver Email' },
        { key: 'message', label: 'Message' },
        { key: 'error', label: 'Error' },
      ]
    : [
        { key: 'timestamp', label: 'Timestamp' },
        { key: 'status', label: 'Status' },
        { key: 'contactId', label: 'Contact ID' },
        { key: 'senderPhoneNumber', label: 'Sender Phone' },
        { key: 'receiverPhoneNumber', label: 'Receiver Phone' },
        { key: 'message', label: 'Message' },
        { key: 'error', label: 'Error' },
      ];

  return (
    <Box>
      {/* Global Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search across all columns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: '100%', md: '50%' },
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
            },
            transition: 'all 0.2s ease-in-out',
            '& .MuiOutlinedInput-input': {
              padding: '12px 16px',
              fontSize: '0.875rem',
            },
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '8px',
          border: '1px solid',
          borderColor: theme.palette.divider,
          overflowX: 'auto',
        }}
      >
        <Table stickyHeader aria-label="Execution Log Table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  onClick={() => handleSort(column.key)}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.background.paper,
                    },
                    textTransform: 'uppercase',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {column.label}
                    {sortColumn === column.key && (
                      <Box component="span" sx={{ marginLeft: 1 }}>
                        {sortOrder === 'asc' ? '▲' : '▼'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            {/* Column Filter Row */}
            <TableRow>
              {columns.map((column) => (
                <TableCell key={`filter-${column.key}`}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={`Filter ${column.label}`}
                    value={columnFilters[column.key] || ''}
                    onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                      },
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedLogs.map((log: any, index: number) => (
              <TableRow key={index}>
                <TableCell>
                  {new Date(log.timestamp).toLocaleString()}
                </TableCell>
                <TableCell>
                  {getExecutionStatusChip(log.status)}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {log.contactId}
                  </Typography>
                </TableCell>
                {isMail ? (
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {log.receiverEmail}
                    </Typography>
                  </TableCell>
                ) : (
                  <>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {log.senderPhoneNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {log.receiverPhoneNumber}
                      </Typography>
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ maxWidth: 200, wordBreak: 'break-word', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => log._id && onOpenLog(log._id)}
                    title="View full message"
                  >
                    {log.message}
                  </Typography>
                </TableCell>
                <TableCell>
                  {log.error ? (
                    <Typography variant="body2" color="error.main" sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                      {log.error}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      -
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredAndSortedLogs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );
};

export const CampaignReport: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { user } = useSelector((state: RootState) => state.authentication);
  const labels = useSelector((state: RootState) => selectLabels(state));

  // CSS keyframes for running animation
  const pulseAnimation = keyframes`
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.7; }
    100% { transform: scale(1); opacity: 1; }
  `;

  // Gear rotation animations for RUNNING status icon
  const rotateClockwise = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `;
  const rotateCounterclockwise = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(-360deg); }
  `;

  const [campaign, setCampaign] = useState<CampaignRunner | null>(null);
  const [campaignStats, setCampaignStats] = useState<CampaignStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Additional state for dataset and user information
  const [datasetCount, setDatasetCount] = useState<number>(0);
  const [dataSetName, setDataSetName] = useState<string | null>(null);
  const [creatorInfo, setCreatorInfo] = useState<User | null>(null);
  const [datasetLoading, setDatasetLoading] = useState(false);
  const [creatorLoading, setCreatorLoading] = useState(false);

  // Table pagination state (for backward compatibility)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog state for viewing full log
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [logDialogLoading, setLogDialogLoading] = useState(false);
  const [logDialogError, setLogDialogError] = useState<string | null>(null);
  const [templateType, setTemplateType] = useState<string | null>(null);
  const [templateDisplayName, setTemplateDisplayName] = useState<string | null>(null);

  const openLogDialog = async (logId: string) => {
    if (!campaignId) return;
    setLogDialogOpen(true);
    setSelectedLog(null);
    setLogDialogError(null);
    setLogDialogLoading(true);
    try {
      const data = await apiClient.get(`/iu/campaignrunner/${campaignId}/executionlog/${logId}`);
      setSelectedLog(data.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setLogDialogError(errorMessage);
      Logger.error('CampaignReport.tsx', `Failed to fetch execution log by id: ${err}`);
    } finally {
      setLogDialogLoading(false);
    }
  };

  const isSmsTemplate = (() => {
    const t = String(templateType || '').toLowerCase();
    return t === 'sms' || t === 'gsmtext';
  })();

  const renderTemplateTypeBadge = () => {
    if (!templateType) return null;

    const baseChipProps = {
      size: 'small' as const,
      sx: {
        fontWeight: 600,
        textTransform: 'none',
      },
    };

    switch (templateType) {
      case 'mail':
        return (
          <Chip
            icon={<EmailIcon />}
            label="Mail"
            color="info"
            variant="filled"
            {...baseChipProps}
          />
        );
      case 'whatsapp':
        return (
          <Chip
            icon={<WhatsAppIcon />}
            label="WhatsApp"
            variant="filled"
            {...baseChipProps}
            sx={{
              ...baseChipProps.sx,
              bgcolor: '#25D366',
              color: '#ffffff',
              '& .MuiChip-icon': { color: '#ffffff' },
            }}
          />
        );
      case 'whatsappbusiness':
        return (
          <Chip
            icon={<WhatsAppIcon />}
            label="WhatsApp"
            variant="filled"
            {...baseChipProps}
            sx={{
              ...baseChipProps.sx,
              bgcolor: '#DAA520',
              color: '#000000',
              '& .MuiChip-icon': { color: '#000000' },
            }}
          />
        );
      case 'sms':
        return (
          <Chip
            icon={<SmsIcon />}
            label="SMS"
            color="secondary"
            variant="filled"
            {...baseChipProps}
            sx={{
              ...baseChipProps.sx,
              bgcolor: 'transparent',
              background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
              color: '#ffffff',
              '& .MuiChip-icon': { color: '#ffffff' }
            }}
          />
        );
      case 'telegram':
        return (
          <Chip
            icon={<TelegramIcon />}
            label="Telegram"
            color="primary"
            variant="filled"
            {...baseChipProps}
          />
        );
      default:
        return null;
    }
  };

  const closeLogDialog = () => {
    setLogDialogOpen(false);
    setSelectedLog(null);
  };

  const fetchTemplateType = async (templateId: string) => {
    try {
      const data = await apiClient.get(`/iu/contenttemplate/${templateId}`);
      const t = data?.data;
      if (t && t.templateType) {
        setTemplateType(String(t.templateType).toLowerCase());
      } else {
        setTemplateType(null);
      }
      const rawName = t?.templateName as string | undefined;
      if (rawName) {
        const cid = user?.companyId;
        let display = rawName;
        if (cid) {
          const parts = rawName.split('-');
          if (parts.length >= 3 && parts[0] === 'IUT' && parts[1] === cid) {
            display = parts.slice(2).join('-');
          }
        }
        setTemplateDisplayName(display);
      } else {
        setTemplateDisplayName(null);
      }
    } catch (err) {
      Logger.error('CampaignReport.tsx', `Failed to fetch template: ${err}`);
      setTemplateType(null);
      setTemplateDisplayName(null);
    }
  };

  const fetchCampaign = async () => {
    if (!campaignId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const data = await apiClient.get(`/iu/campaignrunner/${campaignId}`);
      setCampaign(data.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      Logger.error('CampaignReport.tsx', `Failed to fetch campaign: ${err}`);
    } finally {
      setLoading(false);
    }
  };


  const fetchCampaignStats = async () => {
    if (!campaignId) return;
    
    setStatsLoading(true);
    setStatsError(null);
    
    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const data = await apiClient.get(`/iu/campaignrunner/${campaignId}/stats`);
      setCampaignStats(data.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setStatsError(errorMessage);
      Logger.error('CampaignReport.tsx', `Failed to fetch campaign statistics: ${err}`);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchDatasetCount = async () => {
    if (!campaign?.dataSetId) return;
    
    setDatasetLoading(true);
    
    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const data = await apiClient.get(`/iu/ds/cmp/set/${campaign.dataSetId}/count`);
      setDatasetCount(data.data.contactsCount || 0);
    } catch (err) {
      Logger.error('CampaignReport.tsx', `Failed to fetch dataset count: ${err}`);
      setDatasetCount(0);
    } finally {
      setDatasetLoading(false);
    }
  };

  const fetchDataSetName = async () => {
    if (!campaign?.dataSetId) return;
    try {
      const data = await apiClient.get('/iu/ds/cmp/set');
      const datasets = Array.isArray(data.data) ? data.data : [];
      const match = datasets.find((ds: any) => ds._id === campaign.dataSetId);
      if (match) {
        const raw: string = match.collectionName || '';
        const stripped = raw.replace(/^IU-[^-]+-/, '');
        setDataSetName(stripped || raw);
      }
    } catch (err) {
      Logger.error('CampaignReport.tsx', `Failed to fetch dataset name: ${err}`);
    }
  };

  const fetchCreatorInfo = async () => {
    if (!campaign?.createdBy || !user?.companyId) return;
    
    setCreatorLoading(true);
    
    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const data = await apiClient.get(`/user/all/${user.companyId}`);
      const users = Array.isArray(data.data) ? data.data : [];
      
      // Find the creator user
      const creator = users.find((user: User) => user._id === campaign.createdBy);
      if (creator) {
        setCreatorInfo(creator);
      }
    } catch (err) {
      Logger.error('CampaignReport.tsx', `Failed to fetch creator info: ${err}`);
    } finally {
      setCreatorLoading(false);
    }
  };

  // Domain-aware default message for execution log placeholder
  const defaultExecLogUnavailableMessage: string = useMemo(() => {
    try {
      const host = (window?.location?.hostname || '').toLowerCase();
      if (host.endsWith('instantupdate.ai')) {
        return 'Execution Logs will be available once the notification schedule is completed';
      }
      if (host.endsWith('campaignking.ai')) {
        return 'Execution Logs will be available once the campaign is completed';
      }
    } catch {}
    return 'Execution Logs will be available once the campaign is completed';
  }, []);

  useEffect(() => {
    fetchCampaign();
  }, [campaignId]);

  useEffect(() => {
    if (campaign) {
      if (campaign.templateId) {
        fetchTemplateType(campaign.templateId);
      } else {
        setTemplateType(null);
        setTemplateDisplayName(null);
      }
      // For stopped campaigns, always fetch latest stats to finalize reporting
      if (campaign.status === 'stopped' || campaign.status === 'quota_limit_reached' || campaign.status === 'failed') {
        fetchCampaignStats();
      } else if (campaign.totalContacts === 0 && campaign.processedContacts === 0) {
        // Only fetch stats if not already available in campaign data
        fetchCampaignStats();
      } else {
        // Use stats from campaign data
        setCampaignStats({
          totalContacts: campaign.totalContacts,
          processedContacts: campaign.processedContacts,
          successContacts: campaign.successContacts,
          failedContacts: campaign.failedContacts,
          successRate: campaign.successContacts > 0 ? ((campaign.successContacts / campaign.totalContacts) * 100).toFixed(1) : '0.0'
        });
      }
      
      fetchDatasetCount();
      fetchDataSetName();
      fetchCreatorInfo();
    }
  }, [campaign]);

  const getStatusChip = (status: string) => {
    const rotate = keyframes`
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    `;
    const statusConfig: Record<string, { color: 'warning'|'info'|'success'|'error'|'default'; icon: React.ReactElement; label: string }> = {
      scheduled: { color: 'warning', icon: <ScheduleIcon />, label: 'Scheduled' },
      running: { color: 'info', icon: <PlayArrowIcon />, label: 'Running' },
      queued: { color: 'warning', icon: <ScheduleIcon />, label: 'Queued' },
      completed: { color: 'success', icon: <CheckCircleIcon />, label: 'Completed' },
      cancelled: { color: 'error', icon: <CancelIcon />, label: 'Cancelled' },
      stopped: { color: 'error', icon: <StopIcon />, label: 'Stopped' },
      blocked: { color: 'error', icon: <StopIcon />, label: 'Blocked' },
      failed: { color: 'error', icon: <CancelIcon />, label: 'Failed' },
      awaiting_processing: { color: 'info', icon: <SettingsIcon sx={{ animation: `${rotate} 2s linear infinite` }} />, label: 'Awaiting Processing' },
      quota_limit_reached: { color: 'error', icon: <PauseCircleOutlineIcon />, label: 'Paused : Not enough credits' },
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color as any}
        variant="outlined"
        size="medium"
      />
    );
  };

  const getStatusIcon = (status: string) => {
    const iconSize = 48; // Larger icon size
    const fadeBlink = keyframes`
      0% { opacity: 1; }
      50% { opacity: 0.35; }
      100% { opacity: 1; }
    `;
    
    if (status === 'running') {
      return (
        <Box sx={{ position: 'relative', width: iconSize + 16, height: iconSize + 16 }}>
          {/* Larger gear */}
          <SettingsIcon
            sx={{
              position: 'absolute',
              top: 2,
              left: 2,
              fontSize: iconSize,
              color: theme.palette.info.main,
              animation: `${rotateClockwise} 2.2s linear infinite`,
              transformOrigin: '50% 50%'
            }}
          />
          {/* Smaller interlocked gear */}
          <SettingsIcon
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              fontSize: Math.round(iconSize * 0.65),
              color: theme.palette.info.light,
              animation: `${rotateCounterclockwise} 1.8s linear infinite`,
              transformOrigin: '50% 50%'
            }}
          />
        </Box>
      );
    }

    if (status === 'awaiting_processing') {
      return (
        <Box sx={{ position: 'relative', width: iconSize + 16, height: iconSize + 16 }}>
          <SettingsIcon
            sx={{
              position: 'absolute',
              top: 2,
              left: 2,
              fontSize: iconSize,
              color: theme.palette.info.main,
              animation: `${rotateClockwise} 2.2s linear infinite`,
              transformOrigin: '50% 50%'
            }}
          />
          <SettingsIcon
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              fontSize: Math.round(iconSize * 0.65),
              color: theme.palette.info.light,
              animation: `${rotateCounterclockwise} 1.8s linear infinite`,
              transformOrigin: '50% 50%'
            }}
          />
        </Box>
      );
    }

    const statusIconConfig: Record<string, { icon: React.ReactElement; color: string }> = {
      scheduled: { icon: <ScheduleIcon />, color: theme.palette.warning.main },
      queued: { icon: <ScheduleIcon />, color: theme.palette.warning.main },
      completed: { icon: <CheckCircleOutlineIcon />, color: theme.palette.success.main },
      cancelled: { icon: <CancelIcon />, color: theme.palette.error.main },
      stopped: { icon: <StopIcon />, color: theme.palette.error.main },
      blocked: { icon: <StopIcon />, color: theme.palette.error.main },
      failed: { icon: <CancelIcon />, color: theme.palette.error.main },
      quota_limit_reached: { icon: <PauseCircleOutlineIcon sx={{ animation: `${fadeBlink} 1.6s ease-in-out infinite` }} />, color: theme.palette.error.main },
    };

    const config = statusIconConfig[status as keyof typeof statusIconConfig] || statusIconConfig.scheduled;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {React.cloneElement(config.icon, {
          sx: { ...((config.icon as any)?.props?.sx || {}), fontSize: iconSize, color: config.color }
        })}
      </Box>
    );
  };



  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to get consent filter display text
  const getConsentFilterText = (requireConsent: string) => {
    switch (requireConsent) {
      case 'all':
        return 'All contacts (no consent filter)';
      case 'consent':
        return 'Only contacts with consent';
      case 'my_contacts':
        return 'Only my contacts';
      case 'my_contacts_consent':
        return 'Only my contacts with consent';
      default:
        return 'Unknown consent filter';
    }
  };

  // Helper function to get operator display text
  const getOperatorText = (operator: string) => {
    switch (operator) {
      case 'equals':
        return 'equals';
      case 'not_equals':
        return 'not equals';
      case 'less_than':
        return 'less than';
      case 'less_than_equal':
        return 'less than or equal to';
      case 'greater_than':
        return 'greater than';
      case 'greater_than_equal':
        return 'greater than or equal to';
      case 'contains':
        return 'contains';
      default:
        return operator;
    }
  };

  // Helper function to get operator icon
  const getOperatorIcon = (operator: string) => {
    switch (operator) {
      case 'equals':
        return <CheckIcon />;
      case 'not_equals':
        return <CloseIcon />;
      case 'less_than':
        return <TrendingDownIcon />;
      case 'less_than_equal':
        return <TrendingDownIcon />;
      case 'greater_than':
        return <TrendingUpIcon />;
      case 'greater_than_equal':
        return <TrendingUpIcon />;
      case 'contains':
        return <SearchIcon />;
      default:
        return <CheckIcon />;
    }
  };

  // Prepare data for charts
  const getBarChartData = () => {
    if (!campaignStats) return [];
    
    const t = String(templateType || '').toLowerCase();
    const isSms = t === 'sms' || t === 'gsmtext';
    const isMail = t === 'mail';

    // For SMS: Success = analytics.deliveryTimestamp present; add Read = analytics.readTimestamp present
    const successValue = isSms && campaignStats.deliverySuccessCount !== undefined
      ? campaignStats.deliverySuccessCount
      : campaignStats.successContacts;

    const base = [
      { name: 'Total', value: datasetCount, color: theme.palette.primary.main },
      { name: 'Filtered', value: campaignStats.totalContacts, color: theme.palette.info.main },
      { name: 'Success', value: successValue, color: theme.palette.success.main },
      { name: 'Failed', value: campaignStats.failedContacts, color: theme.palette.error.main },
      { name: 'Skipped', value: campaignStats.skippedContacts || 0, color: theme.palette.warning.main },
      { name: 'Cancelled', value: campaignStats.cancelledContacts || 0, color: theme.palette.grey[500] },
    ];

    // Read bar: Email uses emailReadCount; SMS uses smsReadCount (analytics.readTimestamp)
    if (isMail) {
      base.push({ name: 'Read', value: campaignStats.emailReadCount || 0, color: theme.palette.secondary.main });
    } else if (isSms) {
      base.push({ name: 'Read', value: campaignStats.smsReadCount || 0, color: theme.palette.secondary.main });
    }
    return base;
  };

  const getPieChartData = () => {
    if (!campaignStats || campaignStats.totalContacts === 0) return [];
    
    return [
      { name: 'Successful', value: campaignStats.successContacts, color: theme.palette.success.main },
      { name: 'Failed', value: campaignStats.failedContacts, color: theme.palette.error.main },
    ];
  };

  if (loading) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Backdrop
          sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    );
  }

  if (error || !campaign) {
    return (
      <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Campaign not found'}
        </Alert>
      </Box>
    );
  }

  const showChartsSection =
    campaign.status === 'completed' ||
    campaign.status === 'stopped' ||
    campaign.status === 'awaiting_processing' ||
    campaign.status === 'quota_limit_reached' ||
    campaign.status === 'failed';

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%" mb={3}>
      <DynamicBreadcrumbs items={[
        { itemPath: "/dashboard", itemLabel: 'Home' },
        { itemPath: "/instant-updates/runner", itemLabel: labels?.['modules-instantupdate-runner-report-breadcrumb-runner'] ?? 'Runner' },
        { itemPath: `/instant-updates/runner/report/${campaignId}`, itemLabel: labels?.['modules-instantupdate-runner-report-breadcrumb-report'] ?? 'Campaign Report' }
      ]} />
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Execution Report
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Chip
            icon={<RefreshIcon />}
            label="Refresh"
            onClick={() => {
              fetchCampaign();
              fetchCampaignStats();
            }}
            variant="outlined"
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            icon={<ArrowBackIcon />}
            label="Back to Runner"
            onClick={() => navigate('/instant-updates/runner')}
            variant="outlined"
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      </Box>

      {/* Campaign Title (Full Width) */}
      <Card sx={{ mb: isMobile && templateType ? 1.5 : 3, position: 'relative' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs="auto">
              <Box mr={2}>
                {getStatusIcon(campaign.status)}
              </Box>
            </Grid>
            <Grid item xs>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.heading?.main ?? theme.palette.secondary.main }}>
                {campaign.campaignName}
              </Typography>
              {campaign.description && (
                <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 'normal' }}>
                  {campaign.description}
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
        {templateType && !isMobile && (
          <Box sx={{ position: 'absolute', top: '50%', right: 16, transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            {isSmsTemplate && campaignStats?.totalSmsCreditCost != null && (campaign.status === 'completed' || campaign.status === 'failed') && (
              <Chip
                icon={<LocalOfferIcon />}
                label={`Total Cost: ${campaignStats.totalSmsCreditCost} credit${campaignStats.totalSmsCreditCost === 1 ? '' : 's'}`}
                sx={{
                  fontWeight: 700,
                  bgcolor: theme.palette.success.main,
                  color: '#fff',
                  '& .MuiChip-icon': { color: '#fff' },
                }}
              />
            )}
            {renderTemplateTypeBadge()}
          </Box>
        )}
      </Card>
      {templateType && isMobile && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
            {isSmsTemplate && campaignStats?.totalSmsCreditCost != null && (campaign.status === 'completed' || campaign.status === 'failed') && (
              <Chip
                icon={<LocalOfferIcon />}
                label={`Total Cost: ${campaignStats.totalSmsCreditCost} credit${campaignStats.totalSmsCreditCost === 1 ? '' : 's'}`}
                sx={{
                  fontWeight: 700,
                  bgcolor: theme.palette.success.main,
                  color: '#fff',
                  '& .MuiChip-icon': { color: '#fff' },
                }}
              />
            )}
            {renderTemplateTypeBadge()}
          </CardContent>
        </Card>
      )}

      {/* Campaign Overview */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={6}>
          <CampaignInformation
            campaign={campaign}
            formatUtcToLocal={(d: any) => formatUtcToLocal(d as any)}
            getStatusChip={getStatusChip}
            dataSetName={dataSetName}
            templateDisplayName={templateDisplayName}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {showChartsSection ? (
            <PerformanceReview data={getBarChartData()} />
          ) : (
            <ExecutionStatistics
              campaignStats={campaignStats}
              datasetCount={datasetCount}
              datasetLoading={datasetLoading}
              statsLoading={statsLoading}
              statsError={statsError}
            />
          )}
        </Grid>
      </Grid>

      {/* Campaign Filtering Rules, Execution Statistics (swapped with Performance Overview), and Success vs Failed */}
      {showChartsSection && (
        <Grid container spacing={3} mb={4}>
          {/* Campaign Filtering Rules */}
          {campaign.filters && (
            <Grid item xs={12} lg={4}>
              <FilteringRules filters={campaign.filters} />
            </Grid>
          )}

          {/* Execution Statistics (swapped with Performance Overview in top row) */}
          <Grid item xs={12} md={
              (campaign.status === 'quota_limit_reached' || (campaignStats && ((campaignStats.successContacts || 0) === 0 && (campaignStats.failedContacts || 0) === 0))) ? 12 : 6
            } lg={
              campaign.filters
                ? (
                    (campaign.status === 'quota_limit_reached' || (campaignStats && ((campaignStats.successContacts || 0) === 0 && (campaignStats.failedContacts || 0) === 0)))
                      ? 8
                      : 4
                  )
                : (
                    (campaign.status === 'quota_limit_reached' || (campaignStats && ((campaignStats.successContacts || 0) === 0 && (campaignStats.failedContacts || 0) === 0)))
                      ? 12
                      : 6
                  )
            }>
            <ExecutionStatistics
              campaignStats={campaignStats}
              datasetCount={datasetCount}
              datasetLoading={datasetLoading}
              statsLoading={statsLoading}
              statsError={statsError}
            />
          </Grid>
          
          {/* Success vs Failed (hidden when quota_limit_reached) */}
          {campaign.status !== 'quota_limit_reached' && !(campaignStats && ((campaignStats.successContacts || 0) === 0 && (campaignStats.failedContacts || 0) === 0)) && (
            <Grid item xs={12} md={6} lg={campaign.filters ? 4 : 6}>
              <SuccessVsFailed status={campaign.status} data={getPieChartData()} />
            </Grid>
          )}
        </Grid>
      )}

      {/* Campaign Filtering Rules (standalone when no charts) */}
      {campaign.filters && campaign.status !== 'completed' && campaign.status !== 'stopped' && campaign.status !== 'awaiting_processing' && campaign.status !== 'quota_limit_reached' && campaign.status !== 'failed' && (
        <Box mb={4}>
          <FilteringRules filters={campaign.filters} />
        </Box>
      )}

      {/* Execution Log */}
      {(campaign.status === 'completed' || campaign.status === 'running' || campaign.status === 'stopped' || campaign.status === 'cancelled' || campaign.status === 'awaiting_processing' || campaign.status === 'quota_limit_reached' || campaign.status === 'failed' || campaign.status === 'blocked') && (
        <ExecutionLogPanel
          campaignId={campaignId!}
          status={campaign.status}
          onOpenLog={openLogDialog}
          templateType={templateType}
        />
      )}

      {!(campaign.status === 'completed' || campaign.status === 'running' || campaign.status === 'stopped' || campaign.status === 'cancelled' || campaign.status === 'awaiting_processing' || campaign.status === 'quota_limit_reached' || campaign.status === 'failed' || campaign.status === 'blocked') && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Execution Logs
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {labels?.['modules-instantupdate-runner-report-execution-log-unavailable'] || defaultExecLogUnavailableMessage}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Log Preview Dialog */}
      <Dialog open={logDialogOpen} onClose={closeLogDialog} fullWidth maxWidth="md">
        <DialogTitle>Execution Log Details</DialogTitle>
        <DialogContent dividers>
          {logDialogLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 240 }}>
              <CircularProgress color="inherit" />
            </Box>
          ) : logDialogError ? (
            <Alert severity="error">{logDialogError}</Alert>
          ) : selectedLog ? (
            <Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                {getExecutionStatusChip(selectedLog.status)}
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Contact ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedLog.contactId}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Timestamp</Typography>
                <Typography variant="body2">{new Date(selectedLog.timestamp).toLocaleString()}</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              {(() => {
                const raw = selectedLog.message;
                let parsed: any = null;
                try {
                  parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                } catch {
                  parsed = null;
                }
                const isMail = templateType === 'mail';
                const hasSubjectAndBody = parsed && typeof parsed === 'object' && parsed.subject && parsed.body;

                if (isMail || hasSubjectAndBody) {
                  const subject = hasSubjectAndBody ? parsed.subject : (typeof raw === 'string' ? raw : '');
                  const body = hasSubjectAndBody ? parsed.body : (parsed && parsed.body) ? parsed.body : '';
                  return (
                    <Box>
                      {isMail && selectedLog.receiverEmail && (
                        <Box mb={2}>
                          <Typography variant="subtitle2" color="textSecondary">Receiver Email</Typography>
                          <Box component={Paper} variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>{selectedLog.receiverEmail}</Typography>
                          </Box>
                        </Box>
                      )}
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">Email Subject</Typography>
                        <Box component={Paper} variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{subject}</Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>Email Body</Typography>
                        <Box component={Paper} variant="outlined" sx={{ p: 2 }}>
                          <div dangerouslySetInnerHTML={{ __html: String(body || '') }} />
                        </Box>
                      </Box>
                    </Box>
                  );
                }

                // Non-mail or no subject/body structure: show original message nicely
                return (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Message (Original)</Typography>
                    <Box component={Paper} variant="outlined" sx={{ p: 2, bgcolor: 'background.default', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {(() => {
                        try {
                          const parsedMsg = typeof raw === 'string' ? JSON.parse(raw) : raw;
                          // If object has a content/body, prioritize showing that block rendered; else pretty JSON
                          if (parsedMsg && typeof parsedMsg === 'object' && (parsedMsg.content || parsedMsg.body)) {
                            return <div dangerouslySetInnerHTML={{ __html: String(parsedMsg.content || parsedMsg.body) }} />;
                          }
                          return <pre style={{ margin: 0 }}>{JSON.stringify(parsedMsg, null, 2)}</pre>;
                        } catch {
                          return <Typography variant="body2">{String(raw)}</Typography>;
                        }
                      })()}
                    </Box>
                  </Box>
                );
              })()}
              {selectedLog.error && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="textSecondary">Error</Typography>
                  <Typography variant="body2" color="error.main">{selectedLog.error}</Typography>
                </Box>
              )}
              {selectedLog.analytics?.deliveryTimestamp && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="textSecondary">Delivery Timestamp</Typography>
                  <Typography variant="body2">{new Date(selectedLog.analytics.deliveryTimestamp).toLocaleString()}</Typography>
                </Box>
              )}
              {selectedLog.analytics?.readTimestamp && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="textSecondary">Read Timestamp</Typography>
                  <Typography variant="body2">{new Date(selectedLog.analytics.readTimestamp).toLocaleString()}</Typography>
                </Box>
              )}
              {selectedLog.analytics?.deliveryWebhookPayload && typeof selectedLog.analytics.deliveryWebhookPayload === 'object' && (
                <Box mt={2}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Delivery Webhook Payload</Typography>
                  <Box component={Paper} variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(selectedLog.analytics.deliveryWebhookPayload, null, 2)}
                    </pre>
                  </Box>
                </Box>
              )}
              {isSmsTemplate && selectedLog.smsCreditCost != null && (
                <Box mt={2}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Message Cost</Typography>
                  <Chip
                    icon={<LocalOfferIcon />}
                    label={`${selectedLog.smsCreditCost} credit${selectedLog.smsCreditCost === 1 ? '' : 's'}`}
                    sx={{
                      fontWeight: 700,
                      ...(selectedLog.status === 'success'
                        ? { bgcolor: theme.palette.success.main, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }
                        : selectedLog.status === 'pending'
                          ? { bgcolor: theme.palette.warning.main, color: '#000', '& .MuiChip-icon': { color: '#000' } }
                          : {}),
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLogDialog} color="secondary" variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 